import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtTotals, STATUS_META, formatAxiosError } from "../lib/api";
import { GROUP_STATUS_META } from "../lib/partnerGroups";
import { Button } from "../components/ui/button";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ChevronDown, ChevronUp, Copy, ExternalLink, Trash2 } from "lucide-react";

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;
const day = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

const CANCEL_CONFIRM = {
  brouillon: "Annuler ce dossier groupé ? Les propositions redeviennent des propositions seules, rien n'est supprimé.",
  envoyé: "Annuler ce dossier groupé ? Le lien envoyé au client ne fonctionnera plus. Les propositions redeviennent des propositions seules, rien n'est supprimé.",
};
// Un dossier annulé laisse sa ligne (le lien du client répond par un écran « annulé ») : la supprimer fait répondre « Dossier introuvable ».
const DELETE_CONFIRM = "Supprimer définitivement ce dossier groupé ? Le lien envoyé au client affichera « Dossier introuvable ». Les propositions ne sont pas touchées.";

/** Ce que l'équipe sait de la lecture du dossier par le client : jamais ouvert, email ouvert, consulté N fois ; relancé. */
function seenLine(g) {
  if (g.status === "brouillon") return "Pas encore envoyé";
  if (g.status === "annulé") return g.dissolved_at ? `Annulé le ${day(g.dissolved_at)}` : "Annulé";
  const parts = [];
  if (g.first_viewed_at) parts.push(`Ouvert le ${day(g.first_viewed_at)}`, plural(g.views || 1, "vue", "vues"));
  else if (g.email_opened_at) parts.push("Email ouvert, dossier pas encore consulté");
  else parts.push("Pas encore ouvert");
  if (g.follow_up_sent_at) parts.push(`relancé le ${day(g.follow_up_sent_at)}`);
  return parts.join(" · ");
}

/**
 * Suivi des dossiers groupés pour l'équipe (propriétaire, administrateur) : un dossier = plusieurs propositions d'un même client, signées
 * d'un coup. Lecture seule, sauf l'annulation d'un dossier envoyé que personne n'a signé. L'état et les montants viennent du serveur.
 * Le suivi fin d'une villa (pièces, mandat, chaînes) reste sur sa page de suivi habituelle : chaque villa y renvoie.
 */
export default function TeamGroups() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState({}); // id → { loading, data, error }
  const [busy, setBusy] = useState(null);
  useDocumentTitle("Dossiers groupés");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/proposal-groups");
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) { setError(formatAxiosError(e, "Impossible de charger les dossiers")); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (id) => {
    if (open[id]) { setOpen((cur) => { const next = { ...cur }; delete next[id]; return next; }); return; }
    setOpen((cur) => ({ ...cur, [id]: { loading: true } }));
    try {
      const { data } = await api.get(`/proposal-groups/${id}`);
      setOpen((cur) => (cur[id] ? { ...cur, [id]: { data } } : cur));
    } catch (e) { setOpen((cur) => (cur[id] ? { ...cur, [id]: { error: formatAxiosError(e, "Détail indisponible") } } : cur)); }
  };

  const copy = async (g) => {
    try { await navigator.clipboard.writeText(g.public_url); toast.success("Lien du dossier copié"); }
    catch (e) { toast.error("Copie impossible : sélectionnez le lien à la main"); }
  };

  const cancel = async (g) => {
    if (!window.confirm(CANCEL_CONFIRM[g.status])) return;
    setBusy(g.id);
    try {
      await api.delete(`/proposal-groups/${g.id}`);
      toast.success("Dossier annulé");
      setOpen((cur) => { const next = { ...cur }; delete next[g.id]; return next; });
      await load();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setBusy(null); }
  };

  // Route distincte de l'annulation : un nouvel essai d'annulation ne supprime jamais.
  const remove = async (g) => {
    if (!window.confirm(DELETE_CONFIRM)) return;
    setBusy(g.id);
    try {
      await api.delete(`/proposal-groups/${g.id}/permanent`);
      toast.success("Dossier supprimé");
      setOpen((cur) => { const next = { ...cur }; delete next[g.id]; return next; });
      await load();
    } catch (e) {
      toast.error(formatAxiosError(e));
      await load(); // 404 (déjà supprimé par un collègue) ou 409 : la liste doit refléter l'état réel, pas rester sur une ligne périmée
    } finally { setBusy(null); }
  };

  if (error && !groups) {
    return (
      <div className="p-4 sm:p-6 lg:p-12 max-w-4xl" data-testid="team-groups-error">
        <p className="text-sm text-destructive mb-3">{error}</p>
        <Button variant="outline" onClick={load} data-testid="team-groups-retry" className="rounded-sm min-h-[44px]">Réessayer</Button>
      </div>
    );
  }
  if (!groups) return <div className="p-12 text-muted-foreground">Chargement…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-4xl" data-testid="team-groups-page">
      <div className="mb-6">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Partenaires</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Dossiers groupés</h1>
        <p className="text-sm text-muted-foreground mt-2">Plusieurs propositions d'un même client, envoyées dans un seul dossier et signées d'un coup.</p>
      </div>

      {groups.length === 0 ? (
        <p className="bg-card border border-border rounded-sm px-5 py-8 text-sm text-muted-foreground text-center" data-testid="team-groups-empty">
          Aucun dossier groupé pour le moment. Les partenaires les composent depuis leur portail.
        </p>
      ) : (
        <ul className="space-y-3">
          {groups.map((g) => {
            const meta = GROUP_STATUS_META[g.status] || GROUP_STATUS_META.brouillon;
            const detail = open[g.id];
            const cancellable = g.status === "brouillon" || g.status === "envoyé";
            return (
              <li key={g.id} className="bg-card border border-border rounded-sm p-4 sm:p-5" data-testid={`team-group-${g.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                  <div className="min-w-0 flex-1">
                    <h2 className="font-heading font-bold text-lg break-words">{g.title}</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {g.client_name || "Sans client"} · <span data-testid={`team-group-partner-${g.id}`}>{g.partner_name ? `via ${g.partner_name}` : "—"}</span>
                    </p>
                  </div>
                  <span data-testid={`team-group-status-${g.id}`} className={`shrink-0 text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
                </div>

                <div className="mt-3 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
                  <span>{plural(g.member_count, "villa", "villas")}</span>
                  {g.status !== "brouillon" && g.status !== "annulé" && <span data-testid={`team-group-count-${g.id}`}>{g.signed_count} / {g.member_count} signées</span>}
                  <span className="font-mono font-medium" data-testid={`team-group-amounts-${g.id}`}>{fmtTotals(g.total_one_shot, g.total_recurring, g.currency || "EUR")}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1" data-testid={`team-group-seen-${g.id}`}>{seenLine(g)}</p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => toggle(g.id)} aria-expanded={!!detail} data-testid={`team-group-toggle-${g.id}`}
                    className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-sm border border-border text-sm font-medium hover:bg-muted transition-colors">
                    {detail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {detail ? "Masquer les villas" : "Voir les villas"}
                  </button>
                  {g.public_url && (
                    <button type="button" onClick={() => copy(g)} data-testid={`team-group-copy-${g.id}`}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-sm border border-border text-sm font-medium hover:bg-muted transition-colors">
                      <Copy className="w-4 h-4" /> Copier le lien
                    </button>
                  )}
                  {cancellable && (
                    <button type="button" onClick={() => cancel(g)} disabled={busy !== null} data-testid={`team-group-cancel-${g.id}`}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-medium text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50">
                      <Trash2 className="w-4 h-4" /> {busy === g.id ? "Annulation…" : "Annuler le dossier"}
                    </button>
                  )}
                  {g.status === "annulé" && (
                    <button type="button" onClick={() => remove(g)} disabled={busy !== null} data-testid={`team-group-delete-${g.id}`}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-medium text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50">
                      <Trash2 className="w-4 h-4" /> {busy === g.id ? "Suppression…" : "Supprimer"}
                    </button>
                  )}
                </div>

                {detail?.loading && <p className="text-sm text-muted-foreground mt-3">Chargement…</p>}
                {detail?.error && <p className="text-sm text-destructive mt-3" data-testid={`team-group-detail-error-${g.id}`}>{detail.error}</p>}
                {detail?.data && (
                  <ul className="mt-3 divide-y divide-border border border-border rounded-sm" data-testid={`team-group-members-${g.id}`}>
                    {(detail.data.members || []).map((m) => {
                      const st = STATUS_META[m.status] || STATUS_META.brouillon;
                      return (
                        <li key={m.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5" data-testid={`team-group-member-${m.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium break-words">{m.title}</p>
                            <p className="text-xs text-muted-foreground font-mono">{fmtTotals(m.total_one_shot, m.total_recurring, m.currency || g.currency || "EUR")}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            {m.signed
                              ? <span className="text-[11px] px-2 py-0.5 rounded-sm border font-medium bg-emerald-50 text-emerald-700 border-emerald-200">Signée</span>
                              : <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${st.color}`}>{st.label}</span>}
                            <Link to={`/proposals/${m.id}/analytics`} data-testid={`team-group-member-open-${m.id}`}
                              className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium text-primary hover:underline">
                              Suivi <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                            </Link>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
